import React, { useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import config from 'constants/config'
import isTimeLimited from 'lib/offer/isTimeLimited'
import OfferTimeLimitedText from '../OfferTimeLimitedText'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import { OFFER_TYPE_LAST_MINUTE_SLUG } from 'constants/offer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import { OfferPackageOptionView, getCheapestSelectedPackageOption, getCheapestAvailablePackageOption, arePricesLoading, getFromDurationLabel } from 'selectors/offerPage/offerPageSelectors'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'components/Luxkit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'components/Luxkit/TextLink'
import WalledContent from 'components/Common/WalledContent'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import BodyText from 'components/Luxkit/Typography/BodyText'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import { useBusinessTravellerStickyBudgetBarHeightContext } from 'businessTraveller/utils/useBusinessBudgetBarHeightContext'
import Group from 'components/utils/Group'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueLastMinuteDiscount from 'components/Luxkit/PricePoints/Value/PriceRowValueLastMinuteDiscount'
import PriceRowFlightPackage from 'components/Luxkit/PricePoints/PriceRowFlightPackage'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import { unique } from 'lib/array/arrayUtils'
import * as Analytics from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'

const SummaryPane = styled(Pane)`
  position: relative;

  > * + * {
    margin-top: ${rem(12)};
  }
`

const PackagePrices = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const PriceDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${rem(4)};
`

const FlightPrice = styled(PriceDetails)`
  margin-top: ${rem(8)};
`

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  text-align: center;
`

interface Props {
  offer: App.Offer;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
  rooms: Array<App.Occupants>;
  className?: string;
  cheapestSelectedOption?: OfferPackageOptionView;
  cheapestOption: OfferPackageOptionView;
  onViewPackageOptions: () => void;
  calendarsLoading?: boolean;
  durationLabel: string;
}

function LastMinuteSummary(props: Props) {
  const {
    className,
    onViewPackageOptions,
    offer,
    flightOrigin,
    cheapestSelectedOption,
    cheapestOption,
    rooms,
    flightDestination,
    calendarsLoading,
    durationLabel,
  } = props

  const offerDispatch = useOfferPageDispatch()
  const basePackageView = cheapestSelectedOption ?? cheapestOption
  const {
    hotelPrice,
    flightPrice,
    capacityValid,
    hotelAvailable,
    package: pkg,
  } = basePackageView

  const showPrices = capacityValid && hotelAvailable
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && config.businessTraveller.currentAccountMode !== 'business'
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const availableForPurchase = useIsAvailableForPurchase()
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])

  const onToggleFilter = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerDispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
  }

  const businessTravellerStickyBudgetBarStyle = useBusinessTravellerStickyBudgetBarHeightContext()

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  const shouldTrackPrice = !calendarsLoading && availableForPurchase && showPrices && !!hotelPrice
  const { searchId } = useQsQueryParams<{ searchId?: string }>()
  useEffect(() => {
    if (shouldTrackPrice && searchId && offer?.id) {
      Analytics.trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: hotelPrice.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, hotelPrice, offer?.id])

  return (
    <SummaryPane style={businessTravellerStickyBudgetBarStyle} border="neutral-six" borderRadius="XS" padding="28 20" shadow="bottom-small" className={className} data-testid="SummaryPane">
      {availableForPurchase && <>
        {isTimeLimited(offer) && <OfferTimeLimitedText />}
        <WalledContent enforced={offer.walledGarden} wall={(
          <BodyText variant="large" weight="bold">{offer.durationLabel} packages available</BodyText>
        )}
        >
          {showPrices && (
            <PackagePrices>
              <PriceDetails>
                <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                <PriceRowPrice
                  data-testid="pricing"
                  size="L"
                  price={hotelPrice}
                  saleUnit={offer.saleUnit}
                />
                <PriceRowTaxesAndFees />
                {!!cheapestOption.package.discountPercent && <PriceRowValueLastMinuteDiscount
                  discountPercentage={cheapestOption.package.discountPercent}
                />}
                <Group direction="vertical" horizontalAlign="center" verticalAlign="center" gap={4}>
                  {showIncludedGuestsExceeded && (
                    <AdditionalGuestsPopup
                      complex={false}
                      description="+ Extra guest surcharge"
                      modalContent={pkg.roomPolicyDescription}
                    />
                  )}
                  {flightsEnabled &&
                    <FlightPrice>
                      {!hasFlights && flightOrigin.code === flightDestination?.code && (
                        <ChangeDepartureWrapper>
                          <PriceRowCaption>
                            No flights available from {flightOrigin.name}. Please select another airport.
                          </PriceRowCaption>
                          <TextLink
                            onClick={onToggleFilter}
                            data-filter="airport"
                          >
                            Change departure airport
                          </TextLink>
                        </ChangeDepartureWrapper>
                      )}
                      {!hasFlights && flightOrigin.code !== flightDestination?.code && (
                        <PriceRowCaption>
                          Add flights to the hotel package. Select from all
                          major airlines.
                        </PriceRowCaption>
                      )}
                      {hasFlights && <PriceRowFlightPackage
                        data-testid="FlightPrice"
                        product="hotel"
                        price={hotelPrice + flightPrice}
                      />}
                      <AtolProtectedTooltip />
                    </FlightPrice>
                }
                  {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                    type="estimate"
                    offer={offer}
                    totalCost={hotelPrice}
                    numberOfNights={pkg.duration}
                    roomCount={rooms.length}
                  />}
                  <PriceRowAgentHubCommission
                    size="L"
                    productType={offer.productType}
                    vendorName={vendorName}
                    offerLocations={offerLocations}
                    offerId={offer.id}
                    packageId={pkg.id}
                  />
                </Group>
              </PriceDetails>
              <LoadingIndicator floating backdrop="grey-light" opaque visible={!!calendarsLoading} />
            </PackagePrices>
          )}
        </WalledContent>
        <TextButton
          kind="primary"
          data-testid="OfferAvailable"
          onClick={onViewPackageOptions}
          fit="flex"
        >
          View {config.brandConfig.travelSynonym} options
        </TextButton>
      </>}
      {!availableForPurchase &&
        <>
          <BodyText
            variant="large"
            data-testid="OfferSoldOut"
            weight="bold"
          >
            This offer has SOLD OUT
          </BodyText>
          <TextButton
            kind="primary"
            to={`/${OFFER_TYPE_LAST_MINUTE_SLUG}`}
            fit="flex"
          >
            Browse all Last Minute Escapes
          </TextButton>
        </>
      }
    </SummaryPane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestSelectedOption: getCheapestSelectedPackageOption(appState, state),
  cheapestOption: getCheapestAvailablePackageOption(state, appState),
  rooms: state.rooms,
  offer: state.offer,
  flightOrigin: state.airport,
  calendarsLoading: arePricesLoading(appState, state),
  durationLabel: getFromDurationLabel(state, appState),
}), LastMinuteSummary)
